import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Banner, Button, Stack, Text } from '@figma/fpl-components';
import cssVars from '@figma/fpl-tokens/css-vars';
import * as stylex from '@stylexjs/stylex';
import { usePostHog } from 'posthog-js/react';
import { PageLoader } from '@/components/Common/PageLoader/PageLoader';
import { GenerateComposer } from '@/components/Generate/Composer/GenerateComposer';
import { toMediaAsset } from '@/components/Generate/Composer/inputRules';
import { GenerateHeader } from '@/components/Generate/GenerateHeader';
import { defaultModelFor, useGenerateModelAccess } from '@/components/Generate/generateModelAccess';
import { runErrorBanner, type RunErrorBanner } from '@/components/Generate/runErrorBanner';
import { isToolOutputPlaceholder } from '@/components/Tool/ToolOutputGroups';
import { ToolOutputHistory } from '@/components/Tool/ToolOutputHistory';
import { FF_TOOLS_CATALOG } from '@/consts/featureFlags';
import { AuthContext } from '@/contexts/AuthContext';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useGeneratorRuns, type GeneratorFailure } from '@/hooks/useGeneratorRuns';
import { I18N_KEYS } from '@/language/keys';
import { useGenerateModelQuery, useGenerateModelsQuery } from '@/queries/useGeneratePage';
import { useModelRunCredits } from '@/services/CreditsContext';
import { getSignInReturnPath } from '@/utils/urls';
import type { GenerateMode, GenerateRequest } from '@/api/generate.api';
import type { User } from '@/types/auth.types';

/** Gap between the floating prompt and the bottom of the page, in px. */
const COMPOSER_INSET = 16;
/** The prompt's move between the centre and the bottom. Longer trips take a little longer, as in the prototype. */
const DOCK_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';
const DOCK_MIN_DURATION_MS = 420;
const DOCK_MAX_DURATION_MS = 620;
/** How long the page waits for earlier results before it shows the prompt in the centre anyway. */
const HISTORY_WAIT_MS = 1000;

/**
 * Public generate page at `/generate?model=<modelSlug>`. Signed-out visitors can compose a prompt;
 * the primary button sends them to sign in, the same way the tool page does.
 * Once a run starts, the output gallery fills the page and the prompt floats over its bottom edge.
 * Gated by {@link FF_TOOLS_CATALOG}, like the Toolkit that links here; with the flag off the visitor is
 * sent to the main route.
 */
export function GeneratePage({ user }: { user: User | null }) {
  const { value: isToolsCatalogEnabled } = useFeatureFlag(FF_TOOLS_CATALOG);
  const flagsLoaded = useFlagsLoaded();
  // Started here so the catalog loads alongside the flag; the content reads it from the shared cache.
  useGenerateModelsQuery(Boolean(user));

  // Waits while PostHog has not answered, so allowed visitors are not sent away on a slow load. Once it has,
  // anything but `true` is off: a flag the project does not have stays `undefined` for good.
  if (isToolsCatalogEnabled === undefined && !flagsLoaded) return <PageLoader />;
  if (isToolsCatalogEnabled !== true) return <Navigate to="/" replace />;
  return <GeneratePageContent user={user} />;
}

/** Becomes true a moment after the page opens. */
function useHistoryWaitOver() {
  const [waitOver, setWaitOver] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setWaitOver(true), HISTORY_WAIT_MS);
    return () => clearTimeout(timer);
  }, []);
  return waitOver;
}

/** Becomes true once PostHog has delivered its flags, whether or not they include the one asked about. */
function useFlagsLoaded() {
  const posthog = usePostHog();
  const [flagsLoaded, setFlagsLoaded] = useState(() => posthog.featureFlags.hasLoadedFlags);
  useEffect(() => posthog.onFeatureFlags(() => setFlagsLoaded(true)), [posthog]);
  return flagsLoaded;
}

function GeneratePageContent({ user }: { user: User | null }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const modelSlug = searchParams.get('model');
  const signedIn = Boolean(user);
  const modelsQuery = useGenerateModelsQuery(signedIn);
  const modelQuery = useGenerateModelQuery(modelSlug, signedIn);
  const {
    accessOf,
    canUseAutoDuration,
    withPlanSettings,
    isNew,
    status: accessStatus,
  } = useGenerateModelAccess(signedIn);
  // Shown above the prompt until it is closed or the next run starts.
  const [pageError, setPageError] = useState<RunErrorBanner | null>(null);
  const { enrichUser } = useContext(AuthContext);
  // The server refuses a run for credits only when the balance here is out of date. Reading it again
  // checks the next Generate against the real number. The error still shows, because the credits banner
  // needs a price and some models have none.
  const handleGenerateFailed = useCallback(
    (failure: GeneratorFailure) => {
      if (failure.failed.some((run) => run.creditsRefused)) {
        void enrichUser();
      }
      setPageError(runErrorBanner(failure, t));
    },
    [enrichUser, t],
  );
  const handleBalance = useModelRunCredits();
  const handleHistoryFailed = useCallback(() => {
    setPageError({
      title: t(I18N_KEYS.GENERATE_PAGE.HISTORY_ERROR_TITLE),
      message: t(I18N_KEYS.GENERATE_PAGE.RUN_ERROR_RETRY),
    });
  }, [t]);
  const handleCancelFailed = useCallback(() => {
    setPageError({
      title: t(I18N_KEYS.GENERATE_PAGE.CANCEL_ERROR_TITLE),
      message: t(I18N_KEYS.GENERATE_PAGE.RUN_ERROR_RETRY),
    });
  }, [t]);
  const runs = useGeneratorRuns({
    signedIn,
    onGenerateFailed: handleGenerateFailed,
    onHistoryFailed: handleHistoryFailed,
    onCancelFailed: handleCancelFailed,
    onBalance: handleBalance,
  });
  const { outputs, pendingCount } = runs;
  // Only results that came back can be references. Loading and failed cards have no asset.
  const gallery = useMemo(
    () =>
      outputs.flatMap((output) =>
        isToolOutputPlaceholder(output) || output.type === 'text' ? [] : (toMediaAsset(output) ?? []),
      ),
    [outputs],
  );
  const isRunning = pendingCount > 0;
  const composerRef = useRef<HTMLDivElement>(null);
  const galleryRef = useRef<HTMLDivElement>(null);
  const lastComposerRect = useRef<DOMRect | null>(null);
  const wasDocked = useRef(false);
  const [composerHeight, setComposerHeight] = useState(0);
  // The gallery opens as soon as a run starts, so its loading cards show while the outputs are made.
  // A first Generate the server refuses closes it again and the prompt goes back to the centre.
  // It stays open while older history can still load, even with nothing to show, because "Load more" is
  // inside it. Cancelling every run on the loaded pages can leave it empty.
  const showGallery = outputs.length > 0 || runs.canLoadMore;
  const failed = modelsQuery.isError || modelQuery.isError || accessStatus === 'failed';
  const models = (modelsQuery.data ?? []).filter((option) => accessOf(option) !== 'hidden');
  const lockedModelSlugs = new Set(models.filter((option) => accessOf(option) === 'locked').map(({ slug }) => slug));
  const newModelSlugs = new Set(models.filter(isNew).map(({ slug }) => slug));
  // a link can name a model this user can't pick, which then shows as not available
  const model = modelQuery.data && accessOf(modelQuery.data) === 'available' ? withPlanSettings(modelQuery.data) : null;
  // With earlier results, the page opens straight on the gallery with the prompt at the bottom. So it waits
  // for the first history answer before drawing the prompt, instead of showing it in the centre first.
  // The wait is short: a slow history request must not keep the user from writing a prompt.
  const historyWaitOver = useHistoryWaitOver();
  const layoutReady = runs.historyLoaded || historyWaitOver;
  const requestedMode: GenerateMode = searchParams.get('mode') === 'video' ? 'video' : 'image';
  const defaultSlug =
    modelsQuery.isSuccess && !modelsQuery.isPlaceholderData && accessStatus === 'ready'
      ? defaultModelFor(
          models.filter((option) => accessOf(option) === 'available'),
          requestedMode,
        )?.slug
      : undefined;
  const showComposer = !failed && layoutReady;

  // The loading cards of a new run sit at the top. Results arriving later don't scroll, so "Load more"
  // at the bottom keeps the reader where they are.
  useEffect(() => {
    if (isRunning) {
      galleryRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [isRunning]);

  // The prompt floats over the gallery, so the gallery reserves its height as scroll room at the bottom.
  useLayoutEffect(() => {
    const element = composerRef.current;
    if (!element) return;
    setComposerHeight(element.offsetHeight);
    const observer = new ResizeObserver(() => setComposerHeight(element.offsetHeight));
    observer.observe(element);
    return () => observer.disconnect();
  }, [showComposer]);

  /**
   * When the prompt docks or goes back to the centre, it glides from where it was at the last render. Runs
   * after every render to keep that position current; it only animates when the layout changed.
   */
  useLayoutEffect(() => {
    const element = composerRef.current;
    if (!element) {
      return;
    }
    const from = lastComposerRect.current;
    const to = element.getBoundingClientRect();
    lastComposerRect.current = to;
    const layoutChanged = wasDocked.current !== showGallery;
    wasDocked.current = showGallery;
    if (!layoutChanged || !from || typeof element.animate !== 'function') {
      return;
    }
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }
    const dx = from.left - to.left;
    const dy = from.top - to.top;
    const duration = Math.min(DOCK_MAX_DURATION_MS, DOCK_MIN_DURATION_MS + Math.hypot(dx, dy) / 2);
    element.animate([{ translate: `${dx}px ${dy}px` }, { translate: '0 0' }], { duration, easing: DOCK_EASING });
  });

  // a link with no model opens the default of its `mode`, Image when it has none, and the link then names the model
  useEffect(() => {
    if (modelSlug || !defaultSlug) {
      return;
    }
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete('mode');
        next.set('model', defaultSlug);
        return next;
      },
      { replace: true },
    );
  }, [defaultSlug, modelSlug, setSearchParams]);

  const handleModelChange = useCallback(
    (nextModelSlug: string) => {
      setSearchParams((current) => {
        const next = new URLSearchParams(current);
        next.set('model', nextModelSlug);
        return next;
      });
    },
    [setSearchParams],
  );

  const handleSignIn = useCallback(() => {
    navigate('/signin', { state: { from: getSignInReturnPath() } });
  }, [navigate]);

  const { generate } = runs;
  const handleGenerate = useCallback(
    (request: GenerateRequest) => {
      setPageError(null);
      generate(request);
    },
    [generate],
  );

  const composer = showComposer ? (
    <GenerateComposer
      models={models}
      lockedModelSlugs={lockedModelSlugs}
      newModelSlugs={newModelSlugs}
      model={model}
      requestedMode={requestedMode}
      canUseAutoDuration={model ? canUseAutoDuration(model) : false}
      modelMissing={Boolean(modelSlug) && modelQuery.isSuccess && model === null}
      signedIn={signedIn}
      userId={user?.uid}
      docked={showGallery}
      generating={isRunning || modelQuery.isPlaceholderData || accessStatus === 'loading'}
      onCancel={isRunning ? runs.cancel : undefined}
      onModelChange={handleModelChange}
      onSignIn={handleSignIn}
      onGenerate={handleGenerate}
      gallery={gallery}
    />
  ) : null;

  return (
    <div {...stylex.props(styles.page)} style={{ background: cssVars.color.fsCanvasDefaultFill }}>
      <GenerateHeader signedIn={signedIn} />
      <Stack.Col
        align={showGallery ? 'stretch' : 'center'}
        justify={showGallery ? 'end' : 'center'}
        gap={24}
        style={{
          boxSizing: 'border-box',
          flex: 1,
          minHeight: 0,
          overflow: showGallery ? 'hidden' : undefined,
          padding: showGallery ? 0 : '10px 10px 16px',
          position: 'relative',
        }}
      >
        {!showGallery && layoutReady && (
          <Stack.Col align="center" gap={8}>
            <Text.h1 variant="heading-large" color="brand" align="center" style={{ margin: 0 }}>
              {t(I18N_KEYS.GENERATE_PAGE.TITLE)}
            </Text.h1>
            <Text.p variant="body-large" color="secondary" align="center" style={{ margin: 0 }}>
              {t(I18N_KEYS.GENERATE_PAGE.SUBTITLE)}
            </Text.p>
          </Stack.Col>
        )}

        {failed && (
          <Text.p role="alert" style={{ margin: 0 }}>
            {t(I18N_KEYS.GENERATE_PAGE.LOAD_ERROR)}
          </Text.p>
        )}
        {showGallery && (
          <div {...stylex.props(styles.results)}>
            <div
              ref={galleryRef}
              {...stylex.props(styles.gallery)}
              style={{ paddingBottom: composerHeight + COMPOSER_INSET }}
            >
              <ToolOutputHistory
                results={outputs}
                recipeName={model?.name ?? t(I18N_KEYS.GENERATE_PAGE.GENERATE)}
                onRemoveResults={runs.remove}
                isLoading={false}
                singleImageMaxHeight="65vh"
                placeInOrder
                draggableResults
              />
              {runs.canLoadMore && (
                <div {...stylex.props(styles.loadMore)}>
                  <Button variant="secondary" disabled={runs.isLoadingMore} onClick={runs.loadMore}>
                    {t(I18N_KEYS.GENERATE_PAGE.LOAD_MORE)}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* One slot in both layouts, so docking moves the same prompt (and its draft) rather than remounting it. */}
        {composer && (
          <div ref={composerRef} {...stylex.props(styles.composer, showGallery && styles.composerDocked)}>
            {pageError && (
              <div {...stylex.props(styles.runError)}>
                <Banner.Inset variant="danger" onDismiss={() => setPageError(null)}>
                  <Banner.Message title={pageError.title}>{pageError.message}</Banner.Message>
                </Banner.Inset>
              </div>
            )}
            {composer}
          </div>
        )}
      </Stack.Col>
    </div>
  );
}

/** The gallery fades in a moment after the prompt starts moving, so the two don't compete. */
const revealResults = stylex.keyframes({
  from: { opacity: 0 },
  to: { opacity: 1 },
});

const styles = stylex.create({
  page: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
  },
  results: {
    alignSelf: 'stretch',
    animationDelay: '140ms',
    animationDuration: {
      default: '320ms',
      '@media (prefers-reduced-motion: reduce)': '0s',
    },
    animationFillMode: 'both',
    animationName: revealResults,
    animationTimingFunction: 'ease-out',
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minHeight: 0,
    position: 'relative',
  },
  gallery: {
    boxSizing: 'border-box',
    containerType: 'size',
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minHeight: 0,
    minWidth: 0,
    overflowX: 'hidden',
    overflowY: 'auto',
    width: '100%',
  },
  composer: {
    boxSizing: 'border-box',
    maxWidth: '768px',
    width: '100%',
  },
  runError: {
    marginBottom: '8px',
  },
  loadMore: {
    display: 'flex',
    justifyContent: 'center',
    paddingBlock: '16px',
  },
  // Centred with auto margins, not a transform. A transform would make this box the frame that the
  // prompt's menus position themselves in, and they would open in the wrong place.
  composerDocked: {
    bottom: `${COMPOSER_INSET}px`,
    left: 0,
    marginInline: 'auto',
    position: 'absolute',
    right: 0,
    width: 'calc(100% - 32px)',
  },
});
